import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { Button } from 'devextreme-react/button';
import { SelectBox } from 'devextreme-react/select-box';
import { TextBox } from 'devextreme-react/text-box';
import { NumberBox } from 'devextreme-react/number-box';
import { TextArea } from 'devextreme-react/text-area';
import { Form, Item } from 'devextreme-react/form';
import { Accordion, Item as AccordionItem } from 'devextreme-react/accordion';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { Sortable } from 'devextreme-react/sortable';
import { DataGrid, Column, Editing, RowDragging, Scrolling, Button as Butt, Label, Lookup } from 'devextreme-react/data-grid';
import { TagBox } from 'devextreme-react/tag-box';
import notify from 'devextreme/ui/notify';
import { newToast } from '../../../components';

// Interfaces de TypeScript
interface Parameter {
  id: string;
  KeyValue: string;
  Label: string;
  Type: string;
  Items: string[];
  ColSpan: number;
  Orden: number;
}

interface Group {
  id: string;
  name: string;
  parameters: Parameter[];
}

interface PresetGroup {
  name: string;
  parameters: Omit<Parameter, 'id'>[];
}

interface TypeControl {
  value: string;
  text: string;
}

interface TemplateConfigurationProps {
  IdAnalito: number;
  configPlantillas: string;
  onSave?: (jsonData: any[]) => void;
}

export const TemplateConfigurationImproved: React.FC<TemplateConfigurationProps> = ({ IdAnalito, configPlantillas, onSave }) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [newGroupPopupVisible, setNewGroupPopupVisible] = useState<boolean>(false);
  const [newParameterPopupVisible, setNewParameterPopupVisible] = useState<boolean>(false);
  const [presetGroupPopupVisible, setPresetGroupPopupVisible] = useState<boolean>(false);
  const [newParameterFormData, setNewParameterFormData] = useState({
    KeyValue: '',
    Label: '',
    Type: 'dxTextBox',
    Items: [],
    ColSpan: 1,
    Orden: 1,
  });
  const [currentGroupName, setCurrentGroupName] = useState<string>('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [previousSelectedGroupId, setPreviousSelectedGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState<string>('');
  const [isEditingName, setIsEditingName] = useState<boolean>(false);

  const [importJsonText, setImportJsonText] = useState<string>('');
  const [confirmImportPopupVisible, setConfirmImportPopupVisible] = useState<boolean>(false);
  const [importValidationResult, setImportValidationResult] = useState<{
    isValid: boolean;
    message: string;
    data?: any[];
    stats?: { groups: number; parameters: number };
  } | null>(null);

  // Opciones predefinidas para reutilizar
  const presetItems: string[] = ['AUSENTE', 'CANTIDAD ESCASA', 'CANTIDAD MEDIA', 'CANTIDAD ABUNDANTE', '+', '++', '+++', '++++', 'NORMAL', 'POSITIVO', 'NEGATIVO', 'REACTIVO', 'NO REACTIVO', 'NO SE OBSERVAN'];

  const typeControls: TypeControl[] = [
    { value: 'dxTextBox', text: 'Texto' },
    { value: 'dxTextArea', text: 'Área de texto' },
    { value: 'dxSelectBox', text: 'Lista desplegable' },
    { value: 'dxNumberBox', text: 'Número' },
    { value: 'dxDateBox', text: 'Fecha' },
    { value: 'dxCheckBox', text: 'Casilla de verificación' },
    { value: 'dxRadioGroup', text: 'Grupo de radio' },
    { value: 'dxTagBox', text: 'Selección múltiple' },
  ];

  // Solo mantenemos algunos ejemplos básicos
  const presetGroups: PresetGroup[] = [
    {
      name: 'GRAM MUESTRA VAGINAL',
      parameters: [
        { KeyValue: 'Reacción leucocitaria', Label: 'Reacción leucocitaria', Type: 'dxSelectBox', Items: ['AUSENTE', 'CANTIDAD ESCASA', 'CANTIDAD MEDIA', 'CANTIDAD ABUNDANTE'], ColSpan: 1, Orden: 1 },
        { KeyValue: 'Observaciones', Label: 'Observaciones', Type: 'dxTextArea', Items: [''], ColSpan: 10, Orden: 2 },
      ],
    },
    {
      name: 'MORFOLOGÍA ERITROCITARIA',
      parameters: [{ KeyValue: 'Morfología', Label: 'Morfología', Type: 'dxSelectBox', Items: ['NORMAL'], ColSpan: 2, Orden: 1 }],
    },
  ];

  // Parámetros comunes para botones rápidos
  const commonParameters: Omit<Parameter, 'id'>[] = [
    { KeyValue: 'TipoMuestra', Label: 'Tipo muestra', Type: 'dxSelectBox', Items: ['FLUJO VAGINAL', 'ORINA', 'MATERIA FECAL', 'ESPUTO'], ColSpan: 2, Orden: 1 },
    { KeyValue: 'ReaccionLeucocitaria', Label: 'Reacción leucocitaria', Type: 'dxSelectBox', Items: ['AUSENTE', 'CANTIDAD ESCASA', 'CANTIDAD MEDIA', 'CANTIDAD ABUNDANTE'], ColSpan: 2, Orden: 1 },
    { KeyValue: 'BacilosGramPositivos', Label: 'Bacilos gram positivos', Type: 'dxSelectBox', Items: ['CANTIDAD ESCASA', 'CANTIDAD MEDIA', 'CANTIDAD ABUNDANTE'], ColSpan: 2, Orden: 1 },
    { KeyValue: 'BacilosGramNegativos', Label: 'Bacilos gram negativos', Type: 'dxSelectBox', Items: ['CANTIDAD ESCASA', 'CANTIDAD MEDIA', 'CANTIDAD ABUNDANTE'], ColSpan: 2, Orden: 1 },
    { KeyValue: 'Bacterias', Label: 'Bacterias', Type: 'dxSelectBox', Items: ['CANTIDAD ESCASA', 'CANTIDAD MEDIA', 'CANTIDAD ABUNDANTE'], ColSpan: 2, Orden: 1 },
    { KeyValue: 'Observaciones', Label: 'Observaciones', Type: 'dxTextArea', Items: [''], ColSpan: 2, Orden: 1 },
    { KeyValue: 'Notas', Label: 'Nota', Type: 'dxTextArea', Items: [''], ColSpan: 2, Orden: 1 },
  ];

  // Inicializar desde configPlantillas
  useEffect(() => {
    if (configPlantillas && isInitialLoad) {
      try {
        const parsedConfig = JSON.parse(configPlantillas);
        loadFromJSON(parsedConfig);
        setIsInitialLoad(false);
      } catch (error) {
        console.error('Error parseando configPlantillas:', error);
        setGroups([]);
        setIsInitialLoad(false);
      }
    }
  }, [configPlantillas, isInitialLoad]);

  // Al inicio del componente, después de los states
  const getSortedParameters = useCallback((parameters: Parameter[]) => {
    return [...parameters].sort((a, b) => {
      const ordenA = a.Orden ?? 999;
      const ordenB = b.Orden ?? 999;
      return ordenA - ordenB;
    });
  }, []);

  // Agregar después de los otros useMemo/useCallback

  const loadFromJSON = (jsonData: any[]) => {
    try {
      if (!jsonData || !Array.isArray(jsonData) || jsonData.length === 0) {
        setGroups([]);
        setSelectedGroupId(null);
        return;
      }

      const data = jsonData;
      const groupedData: { [key: string]: Group } = {};

      data.forEach((item, index) => {
        const groupKey = item.Group || `Grupo ${index + 1}`;
        if (!groupedData[groupKey]) {
          groupedData[groupKey] = {
            id: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${Object.keys(groupedData).length}`,
            name: groupKey,
            parameters: [],
          };
        }

        groupedData[groupKey].parameters.push({
          id: `param_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`,
          KeyValue: item.KeyValue || '',
          Label: item.Label || '',
          Type: item.Type || 'dxTextBox',
          Items: Array.isArray(item.Items) ? item.Items : [],
          ColSpan: item.ColSpan || 1,
          Orden: groupedData[groupKey].parameters.length + 1,
        });
      });

      const groupsArray = Object.values(groupedData);

      setGroups(groupsArray);
      if (groupsArray.length > 0) {
        setSelectedGroupId(groupsArray[0].id);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      notify('Error al cargar plantilla: ' + errorMessage, 'error', 3000);
      console.error('Error en loadFromJSON:', error);
    }
  };

  const generateJSON = () => {
    const result: any[] = [];
    groups.forEach((group) => {
      // 👇 Usar la función helper
      getSortedParameters(group.parameters).forEach((param) => {
        result.push({
          Group: group.name,
          KeyValue: param.KeyValue,
          Type: param.Type,
          Label: param.Label,
          Items: param.Items,
          ColSpan: param.ColSpan,
        });
      });
    });
    return result;
  };

  // Al abrir popup:
  const openNewGroupPopup = () => {
    setPreviousSelectedGroupId(selectedGroupId); // ✅ Guardar el actual
    setSelectedGroupId(null); // ✅ Desseleccionar
    setNewGroupPopupVisible(true);
  };

  // Al cerrar popup SIN crear grupo:
  const closeNewGroupPopup = () => {
    setNewGroupPopupVisible(false);
    setCurrentGroupName('');
  };

  // Al crear grupo:
  const addGroup = (groupName: string = '') => {
    if (!groupName || !groupName.trim()) {
      newToast('WARNING', 'Por favor ingrese un nombre para el grupo', 2);
      return;
    }

    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const uniqueId = `new_group_${timestamp}_${random}`;

    const newGroup: Group = {
      id: uniqueId,
      name: groupName || `Nuevo Grupo ${groups.length + 1}`,
      parameters: [],
    };

    setGroups((prevGroups) => {
      const newGroups = [...prevGroups, newGroup];
      return newGroups;
    });

    setSelectedGroupId(uniqueId);

    setCurrentGroupName('');
    setNewGroupPopupVisible(false);
    setPreviousSelectedGroupId(null);
  };

  const addPresetGroup = (presetGroup: PresetGroup) => {
    const uniqueId = `preset_group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newGroup: Group = {
      id: uniqueId, // ← Usar ID único consistente
      name: presetGroup.name,
      parameters: presetGroup.parameters.map((p, index) => ({
        ...p,
        id: `preset_param_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`,
      })),
    };

    setGroups((prevGroups) => [...prevGroups, newGroup]);

    setSelectedGroupId(uniqueId);
    setPresetGroupPopupVisible(false);
    notify(`Grupo "${presetGroup.name}" agregado exitosamente`, 'success', 3000);
  };

  const deleteGroup = (groupId: string) => {
    setGroups((prevGroups) => {
      const newGroups = prevGroups.filter((g) => g.id !== groupId);
      return newGroups;
    });

    if (selectedGroupId === groupId) {
      setSelectedGroupId(null);
    }
    notify('Grupo eliminado', 'success', 2000);
  };

  const duplicateGroup = (groupId: string) => {
    const groupToDuplicate = groups.find((g) => g.id === groupId);
    if (groupToDuplicate) {
      const newGroup: Group = {
        ...groupToDuplicate,
        id: Date.now().toString(),
        name: `${groupToDuplicate.name} (Copia)`,
        parameters: groupToDuplicate.parameters.map((p) => ({
          ...p,
          id: Date.now().toString() + Math.random(),
        })),
      };
      setGroups((prevGroups) => [...prevGroups, newGroup]);
      setSelectedGroupId(newGroup.id);
      notify('Grupo duplicado exitosamente', 'success', 3000);
    }
  };

  const updateGroupName = useCallback(
    (groupId: string, newName: string) => {
      // Validar que el nombre realmente cambió
      const currentGroup = groups.find((g) => g.id === groupId);
      if (!currentGroup || currentGroup.name === newName) {
        return;
      }

      setGroups((prevGroups) => {
        const updated = prevGroups.map((g) => {
          if (g.id === groupId) {
            return { ...g, name: newName };
          }
          return g;
        });

        return updated;
      });
    },
    [groups]
  );

  const addParameter = (groupId: string, paramData: Parameter | null = null) => {
    const group = groups.find((g) => g.id === groupId);
    const nextOrden = group ? group.parameters.length + 1 : 1;

    const newParam: Parameter = paramData || {
      id: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9),
      KeyValue: newParameterFormData.KeyValue || 'NuevaLlave',
      Label: newParameterFormData.Label || 'Nuevo nombre',
      Type: newParameterFormData.Type || 'dxTextBox',
      Items: newParameterFormData.Items || [],
      ColSpan: newParameterFormData.ColSpan || 2,
      Orden: nextOrden,
    };

    setGroups((prevGroups) => {
      const newGroups = prevGroups.map((g) => (g.id === groupId ? { ...g, parameters: [...g.parameters, newParam] } : g));
      return newGroups;
    });

    // Limpiar formulario
    setNewParameterFormData({
      KeyValue: '',
      Label: '',
      Type: 'dxTextBox',
      Items: [],
      ColSpan: 2,
      Orden: 1,
    });

    setNewParameterPopupVisible(false);
  };

  const addCommonParameter = (groupId: string, commonParam: Omit<Parameter, 'id'>) => {
    const newParam: Parameter = {
      ...commonParam,
      id: Date.now().toString() + Math.random(),
    };
    addParameter(groupId, newParam);
  };

  const updateParameter = useCallback((groupId: string, paramId: string, field: string, value: any) => {
    setGroups((prevGroups) => {
      const newGroups = prevGroups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              parameters: g.parameters.map((p) => {
                if (p.id === paramId) {
                  return { ...p, [field]: value };
                }
                return p;
              }),
            }
          : g
      );
      return newGroups;
    });
  }, []);

  const deleteParameter = (groupId: string, paramId: string) => {
    setGroups((prevGroups) => prevGroups.map((g) => (g.id === groupId ? { ...g, parameters: g.parameters.filter((p) => p.id !== paramId) } : g)));
    newToast('SUCCESS', 'Parámetro eliminado', 5);
  };

  const duplicateParameter = (groupId: string, paramId: string) => {
    const group = groups.find((g) => g.id === groupId);
    const param = group?.parameters.find((p) => p.id === paramId);

    if (!param) {
      notify('Parámetro no encontrado', 'error', 2000);
      return;
    }

    // Calcular el siguiente orden basado en el máximo actual
    const maxOrden = group?.parameters.reduce((max, p) => Math.max(max, p.Orden || 0), 0);
    const nextOrden = maxOrden ? maxOrden + 1 : 1;

    // Crear la copia con nuevo ID y orden actualizado
    const newParam: Parameter = {
      ...param,
      id: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9),
      KeyValue: `${param.KeyValue}_copia`,
      Label: `${param.Label} (Copia)`,
      Orden: nextOrden,
    };

    // Actualizar el estado agregando el nuevo parámetro
    setGroups((prevGroups) => {
      return prevGroups.map((g) => (g.id === groupId ? { ...g, parameters: [...g.parameters, newParam] } : g));
    });
  };

  const saveTemplate = () => {
    try {
      const json = generateJSON();
      if (onSave) {
        onSave(json);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      newToast('ERROR', 'Error al guardar plantilla: ' + errorMessage, 5);
    }
  };

  const exportTemplate = () => {
    try {
      const json = generateJSON();
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plantilla_${IdAnalito}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify('Plantilla exportada exitosamente', 'success', 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      notify('Error al exportar: ' + errorMessage, 'error', 3000);
    }
  };

  const validateImportJson = useCallback((jsonText: string) => {
    try {
      if (!jsonText.trim()) {
        setImportValidationResult(null);
        return;
      }

      const parsed = JSON.parse(jsonText);

      // Validar que sea un array
      if (!Array.isArray(parsed)) {
        setImportValidationResult({
          isValid: false,
          message: 'El JSON debe ser un array de objetos',
        });
        return;
      }

      // Validar estructura de cada elemento
      const validationErrors: string[] = [];
      const requiredFields = ['Group', 'KeyValue', 'Label', 'Type'];

      parsed.forEach((item, index) => {
        if (typeof item !== 'object' || item === null) {
          validationErrors.push(`Elemento ${index + 1}: debe ser un objeto`);
          return;
        }

        requiredFields.forEach((field) => {
          if (!item.hasOwnProperty(field) || !item[field]) {
            validationErrors.push(`Elemento ${index + 1}: falta el campo "${field}"`);
          }
        });

        // Validar tipo de control
        const validTypes = ['dxTextBox', 'dxTextArea', 'dxSelectBox', 'dxNumberBox', 'dxDateBox', 'dxCheckBox', 'dxRadioGroup', 'dxTagBox'];
        if (item.Type && !validTypes.includes(item.Type)) {
          validationErrors.push(`Elemento ${index + 1}: tipo de control "${item.Type}" no válido`);
        }

        // Validar ColSpan
        if (item.ColSpan && (typeof item.ColSpan !== 'number' || item.ColSpan < 1 || item.ColSpan > 10)) {
          validationErrors.push(`Elemento ${index + 1}: ColSpan debe ser un número entre 1 y 10`);
        }

        // Validar Items (debe ser array si existe)
        if (item.Items && !Array.isArray(item.Items)) {
          validationErrors.push(`Elemento ${index + 1}: Items debe ser un array`);
        }
      });

      if (validationErrors.length > 0) {
        setImportValidationResult({
          isValid: false,
          message: `Errores encontrados:\n${validationErrors.slice(0, 5).join('\n')}${validationErrors.length > 5 ? `\n... y ${validationErrors.length - 5} errores más` : ''}`,
        });
        return;
      }

      // Calcular estadísticas usando Array.from()
      const groupNames = Array.from(new Set(parsed.map((item: any) => item.Group)));
      const stats = {
        groups: groupNames.length,
        parameters: parsed.length,
      };

      setImportValidationResult({
        isValid: true,
        message: `✅ JSON válido\n📊 ${stats.groups} grupos, ${stats.parameters} parámetros\n📋 Grupos: ${groupNames.join(', ')}`,
        data: parsed,
        stats,
      });
    } catch (error) {
      setImportValidationResult({
        isValid: false,
        message: `Error de sintaxis JSON: ${error instanceof Error ? error.message : 'Error desconocido'}`,
      });
    }
  }, []);

  // Función para importar el JSON validado
  const importFromJson = useCallback(() => {
    if (!importValidationResult?.isValid || !importValidationResult.data) {
      notify('No hay datos válidos para importar', 'warning', 3000);
      return;
    }

    // Mostrar popup de confirmación en lugar del alert
    setConfirmImportPopupVisible(true);
  }, [importValidationResult]);

  // 3. FUNCIÓN para confirmar la importación
  const confirmImport = useCallback(() => {
    if (!importValidationResult?.data) return;

    try {
      loadFromJSON(importValidationResult.data);
      setImportJsonText('');
      setImportValidationResult(null);
      setConfirmImportPopupVisible(false);
      notify('Plantilla importada exitosamente', 'success', 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      notify('Error al importar: ' + errorMessage, 'error', 3000);
      setConfirmImportPopupVisible(false);
    }
  }, [importValidationResult, loadFromJSON]);

  const cancelImport = useCallback(() => {
    setConfirmImportPopupVisible(false);
  }, []);

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);

  // Agregar este useEffect para sincronizar el estado de edición
  useEffect(() => {
    if (selectedGroup && !isEditingName) {
      setEditingGroupName(selectedGroup.name);
    }
  }, [selectedGroup?.id, selectedGroup?.name, isEditingName]);

  const reorderGroups = useCallback((fromIndex: number, toIndex: number) => {
    setGroups((prevGroups) => {
      const newGroups = [...prevGroups];
      const [movedGroup] = newGroups.splice(fromIndex, 1);
      newGroups.splice(toIndex, 0, movedGroup);
      notify(`Grupo "${movedGroup.name}" movido`, 'success', 2000);

      return newGroups;
    });
  }, []);

  return (
    <div>
      <TabPanel selectedIndex={activeTab} onSelectionChanged={(e: any) => setActiveTab(e.selectedIndex)}>
        <TabItem title='Constructor Visual' icon='edit'>
          <div style={{ display: 'flex', height: '100%' }}>
            {/* Panel izquierdo - Lista de grupos */}
            <div style={{ width: '280px', borderRight: '1px solid #e0e0e0', backgroundColor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
              {/* Header del panel de grupos */}
              <div
                style={{
                  padding: '15px',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#fff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                <div>
                  <strong style={{ fontSize: '16px' }}>Grupos</strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    {groups.length} {groups.length === 1 ? 'grupo' : 'grupos'}
                  </div>
                </div>
                <Button icon='plus' stylingMode='contained' type='default' onClick={openNewGroupPopup} hint='Agregar nuevo grupo' width={32} height={32} />
              </div>

              {/* Lista de grupos */}
              <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
                {groups.length > 0 ? (
                  <Sortable
                    data={groups}
                    itemOrientation='vertical'
                    onReorder={(e: any) => {
                      reorderGroups(e.fromIndex, e.toIndex);
                    }}>
                    {groups.map((group, index) => (
                      <div
                        key={group.id}
                        style={{
                          margin: '4px 0',
                          padding: '12px',
                          backgroundColor: selectedGroupId === group.id ? '#e3f2fd' : '#fff',
                          border: selectedGroupId === group.id ? '2px solid #2196f3' : '1px solid #e0e0e0',
                          borderRadius: '6px',
                          cursor: 'move', // ← Cambié a 'move' para indicar que se puede arrastrar
                          transition: 'all 0.2s ease',
                          boxShadow: selectedGroupId === group.id ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                          position: 'relative',
                        }}
                        onClick={() => {
                          setSelectedGroupId(group.id);
                        }}>
                        {/* Handle visual (solo decorativo) */}
                        <div
                          style={{
                            position: 'absolute',
                            left: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#999',
                            fontSize: '14px',
                            padding: '4px',
                            userSelect: 'none',
                            pointerEvents: 'none', // ← No interfiere con el arrastre
                          }}
                          title='Arrastrar para reordenar'>
                          ⋮⋮
                        </div>

                        {/* Contenido del grupo */}
                        <div style={{ marginLeft: '24px' }}>
                          <div
                            style={{
                              fontWeight: selectedGroupId === group.id ? 'bold' : '600',
                              fontSize: '14px',
                              color: selectedGroupId === group.id ? '#1976d2' : '#333',
                              marginBottom: '6px',
                              wordBreak: 'break-word',
                            }}>
                            {group.name}
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginTop: '8px',
                            }}>
                            <div
                              style={{
                                fontSize: '11px',
                                color: '#666',
                                backgroundColor: selectedGroupId === group.id ? 'rgba(33, 150, 243, 0.1)' : '#f5f5f5',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                border: selectedGroupId === group.id ? '1px solid rgba(33, 150, 243, 0.3)' : '1px solid #e0e0e0',
                              }}>
                              {group.parameters.length} parámetro{group.parameters.length !== 1 ? 's' : ''}
                            </div>

                            {/* Botones de acción */}
                            <div
                              style={{ display: 'flex', gap: '2px' }}
                              onClick={(e) => e.stopPropagation()} // ← Evita conflictos con el arrastre
                            >
                              <Button
                                icon='copy'
                                stylingMode='text'
                                onClick={(e: any) => {
                                  e.event?.stopPropagation();
                                  duplicateGroup(group.id);
                                }}
                                hint='Duplicar grupo'
                                width={24}
                                height={24}
                              />
                              <Button
                                icon='trash'
                                stylingMode='text'
                                onClick={(e: any) => {
                                  e.event?.stopPropagation();
                                  deleteGroup(group.id);
                                }}
                                hint='Eliminar grupo'
                                width={24}
                                height={24}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Sortable>
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: '#999',
                    }}>
                    <div
                      style={{
                        fontSize: '48px',
                        marginBottom: '16px',
                        opacity: 0.3,
                      }}>
                      📋
                    </div>
                    <div
                      style={{
                        fontSize: '14px',
                        marginBottom: '16px',
                        lineHeight: '1.4',
                      }}>
                      No hay grupos configurados
                    </div>
                    <Button text='Crear primer grupo' type='default' stylingMode='contained' onClick={() => setNewGroupPopupVisible(true)} width='100%' />
                  </div>
                )}
              </div>
            </div>

            {/* Panel derecho - Editor del grupo seleccionado */}
            <div
              style={{
                flex: 1,
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                minWidth: '600px',
                width: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}>
              {selectedGroup ? (
                <>
                  {/* Header del grupo seleccionado */}
                  <div
                    style={{
                      padding: '20px',
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: '#f8f9fa',
                      minWidth: '560px',
                      flexShrink: 0,
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <TextBox
                          value={selectedGroup?.name || ''}
                          onFocusOut={(e: any) => {
                            const newValue = e.component.option('value');
                            if (selectedGroup?.id === selectedGroupId && newValue && newValue !== selectedGroup.name) {
                              updateGroupName(selectedGroup.id, newValue);
                            }
                          }}
                          onEnterKey={(e: any) => {
                            const newValue = e.component.option('value');
                            if (selectedGroup?.id === selectedGroupId && newValue && newValue !== selectedGroup.name) {
                              updateGroupName(selectedGroup.id, newValue);
                            }
                            e.component.getInputElement().blur();
                          }}
                          placeholder='Nombre del grupo'
                          stylingMode='outlined'
                          inputAttr={{ style: { fontSize: '18px', fontWeight: '600' } }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: '14px',
                          color: '#666',
                          backgroundColor: '#fff',
                          padding: '8px 12px',
                          borderRadius: '20px',
                          border: '1px solid #e0e0e0',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}>
                        {selectedGroup.parameters.length} parámetro{selectedGroup.parameters.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {/* Sección de agregar parámetros */}
                  <div
                    style={{
                      padding: '20px',
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: '#fff',
                      minWidth: '560px',
                      flexShrink: 0,
                    }}>
                    <div style={{ marginBottom: '12px' }}>
                      <strong style={{ fontSize: '16px', color: '#333' }}>Agregar parámetros</strong>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        alignItems: 'center',
                      }}>
                      <Button text='Personalizado' icon='plus' type='default' stylingMode='outlined' onClick={() => setNewParameterPopupVisible(true)} />

                      <div
                        style={{
                          height: '24px',
                          width: '1px',
                          backgroundColor: '#e0e0e0',
                          margin: '0 8px',
                        }}
                      />

                      {commonParameters.map((param, index) => (
                        <Button
                          key={index}
                          text={param.Label}
                          type='default'
                          stylingMode='text'
                          onClick={() => addCommonParameter(selectedGroup.id, param)}
                          style={{
                            backgroundColor: '#f0f7ff',
                            border: '1px solid #bbdefb',
                            borderRadius: '16px',
                            fontSize: '12px',
                            flexShrink: 0,
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Lista de parámetros */}
                  <div style={{ flex: 1, padding: '20px', overflow: 'auto', minWidth: '600px' }}>
                    {selectedGroup.parameters.length > 0 ? (
                      <div
                        style={{
                          minWidth: '800px',
                          width: '100%',
                        }}>
                        <DataGrid
                          key={selectedGroup?.id}
                          keyExpr='id'
                          dataSource={selectedGroup?.parameters ? [...selectedGroup.parameters].sort((a, b) => (a.Orden ?? 999) - (b.Orden ?? 999)) : []}
                          showBorders={true}
                          rowAlternationEnabled={true}
                          columnAutoWidth={false}
                          showColumnLines={true}
                          showRowLines={true}
                          hoverStateEnabled={true}
                          wordWrapEnabled={true}
                          width='100%'
                          height='700px'
                          repaintChangesOnly={false} // 👈 AGREGAR ESTO - forzar re-render completo
                          onRowUpdating={(e: any) => {
                            if (selectedGroup && e.key) {
                              const updatedData = { ...e.oldData, ...e.newData };
                              updateParameter(selectedGroup.id, e.key, 'Items', updatedData.Items);
                              updateParameter(selectedGroup.id, e.key, 'KeyValue', updatedData.KeyValue);
                              updateParameter(selectedGroup.id, e.key, 'Label', updatedData.Label);
                              updateParameter(selectedGroup.id, e.key, 'Type', updatedData.Type);
                              updateParameter(selectedGroup.id, e.key, 'ColSpan', updatedData.ColSpan);
                              updateParameter(selectedGroup.id, e.key, 'Orden', updatedData.Orden);
                            }
                          }}
                          style={{
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}>
                          <Scrolling columnRenderingMode='standard' mode='virtual' />
                          <RowDragging
                            allowReordering={true}
                            onReorder={(e: any) => {
                              if (!selectedGroup) {
                                return;
                              }

                              const currentSortedParams = [...selectedGroup.parameters].sort((a, b) => (a.Orden ?? 999) - (b.Orden ?? 999));

                              const reorderedParams = [...currentSortedParams];
                              const [movedItem] = reorderedParams.splice(e.fromIndex, 1);
                              reorderedParams.splice(e.toIndex, 0, movedItem);

                              const updatedParameters = reorderedParams.map((param, idx) => ({
                                ...param,
                                Orden: idx + 1,
                              }));

                              setGroups((prevGroups) =>
                                prevGroups.map((g) => {
                                  if (g.id === selectedGroup.id) {
                                    return { ...g, parameters: updatedParameters };
                                  }
                                  return g;
                                })
                              );
                            }}
                            showDragIcons={true}
                          />
                          <Editing
                            mode='popup'
                            allowUpdating={true}
                            allowDeleting={true}
                            useIcons={true}
                            popup={{
                              title: 'Editar Parámetro',
                              showTitle: true,
                              width: 700,
                              height: 500,
                            }}
                            form={{
                              colCount: 2,
                              labelLocation: 'top',
                            }}
                          />
                          <Column dataField='KeyValue' caption='KEY' minWidth={120} />
                          <Column dataField='Label' caption='NOMBRE' minWidth={140} />
                          <Column dataField='Type' caption='TIPO CONTROL' width={120}>
                            <Lookup dataSource={typeControls} valueExpr='value' displayExpr='text' />
                          </Column>
                          <Column
                            dataField='Items'
                            caption='OPCIONES'
                            width={200}
                            allowSorting={false}
                            cellRender={(data: any) => {
                              const items = Array.isArray(data.value) ? data.value : [];
                              return (
                                <div
                                  style={{
                                    fontSize: '12px',
                                    color: '#666',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    lineHeight: '1.4',
                                    padding: '4px 0',
                                    maxWidth: '100%',
                                  }}>
                                  {items.length > 0 ? items.join(', ') : 'Sin opciones'}
                                </div>
                              );
                            }}
                            editCellRender={(data: any) => (
                              <TagBox
                                dataSource={presetItems}
                                value={Array.isArray(data.value) ? data.value : []}
                                onValueChanged={(e: any) => {
                                  // ✅ IMPORTANTE: Actualizar inmediatamente el estado
                                  if (selectedGroup && data.row && data.row.key) {
                                    updateParameter(selectedGroup.id, data.row.key, 'Items', e.value || []);
                                  }

                                  // También actualizar el valor del DataGrid
                                  data.setValue(e.value || []);
                                }}
                                acceptCustomValue={true}
                                placeholder='Agregue opciones...'
                                width='100%'
                                searchEnabled={true}
                                showSelectionControls={true}
                                applyValueMode='instantly' // ✅ Aplicar cambios inmediatamente
                              />
                            )}
                            formItem={{
                              colSpan: 2,
                              editorType: 'dxTagBox',
                              editorOptions: {
                                dataSource: presetItems,
                                acceptCustomValue: true,
                                placeholder: 'Agregue las opciones del parámetro',
                                multiline: true,
                                height: 120,
                                searchEnabled: true,
                                showSelectionControls: true,
                                onValueChanged: (e: any) => {
                                  console.log('');
                                  // Este se maneja automáticamente por el DataGrid
                                },
                              },
                            }}
                          />
                          <Column dataField='Orden' caption='ORD' dataType='number' width={70} alignment='center' allowSorting={true} sortOrder='asc' />
                          <Column dataField='ColSpan' caption='COL' dataType='number' width={60} alignment='center' />
                          <Column type='buttons' caption={'ACCIÓN'} width={110}>
                            <Butt name='edit' />
                            <Butt name='delete' hint='Eliminar parámetro' onClick={(e) => deleteParameter(selectedGroup.id, e.row?.data.id)} />
                            <Butt icon='copy' hint='Duplicar parámetro' onClick={(e) => duplicateParameter(selectedGroup.id, e.row?.data.id)} />
                          </Column>
                        </DataGrid>
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '60px 20px',
                          color: '#999',
                        }}>
                        <div
                          style={{
                            fontSize: '48px',
                            marginBottom: '16px',
                            opacity: 0.3,
                          }}>
                          ⚙️
                        </div>
                        <div
                          style={{
                            fontSize: '16px',
                            marginBottom: '8px',
                            fontWeight: '500',
                          }}>
                          No hay parámetros en este grupo
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '20px',
                          }}>
                          Agrega parámetros usando los botones de arriba
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    textAlign: 'center',
                    color: '#999',
                    backgroundColor: '#fafafa',
                  }}>
                  <div>
                    <div
                      style={{
                        fontSize: '64px',
                        marginBottom: '20px',
                        opacity: 0.3,
                      }}>
                      📝
                    </div>
                    <div
                      style={{
                        fontSize: '18px',
                        marginBottom: '8px',
                        fontWeight: '500',
                      }}>
                      Selecciona un grupo para editar
                    </div>
                    <div style={{ fontSize: '14px' }}>O crea un nuevo grupo para comenzar</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabItem>

        <TabItem title='Vista Previa' icon='eyeopen'>
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '14px', color: '#666', padding: '5px 10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                {groups.length} grupos • {groups.reduce((acc, g) => acc + g.parameters.length, 0)} parámetros
              </div>
            </div>
            <Accordion multiple={true} collapsible={true} style={{ width: '100%', overflowY: 'auto', maxHeight: '70vh' }}>
              {groups.map((group) => (
                <AccordionItem key={group.id} title={group.name}>
                  <Form colCount={2} labelLocation='top'>
                    {/* 👇 Usar la función helper */}
                    {getSortedParameters(group.parameters).map((param) => (
                      <Item key={param.id} colSpan={Math.min(param.ColSpan, 2)} label={{ text: param.Label }}>
                        {param.Type === 'dxSelectBox' && <SelectBox dataSource={param.Items} placeholder='Seleccionar...' disabled={true} />}
                        {param.Type === 'dxTextBox' && <TextBox placeholder='Texto...' disabled={true} />}
                        {param.Type === 'dxTextArea' && <TextArea placeholder='Observaciones...' disabled={true} />}
                        {param.Type === 'dxNumberBox' && <NumberBox placeholder='0' disabled={true} />}
                        {param.Type === 'dxTagBox' && <TagBox dataSource={param.Items} placeholder='Selección múltiple...' disabled={true} />}
                      </Item>
                    ))}
                  </Form>
                </AccordionItem>
              ))}
            </Accordion>
            {groups.length === 0 && <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>No hay grupos para mostrar en la vista previa</div>}
          </div>
        </TabItem>

        <TabItem title='JSON' icon='export'>
          <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Sección de Exportación */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Exportar Plantilla</h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button
                    text='Copiar JSON'
                    icon='copy'
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(generateJSON(), null, 2));
                      notify('JSON copiado al portapapeles', 'success', 2000);
                    }}
                  />
                  <Button text='Descargar Archivo' icon='export' onClick={exportTemplate} />
                </div>
              </div>

              <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px', marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Estadísticas:</strong> {groups.length} grupos, {groups.reduce((acc, g) => acc + g.parameters.length, 0)} parámetros total
                <br />
                <small>Este JSON es compatible con tu sistema actual y se guarda en el campo An_Plantilla</small>
              </div>

              <TextArea value={JSON.stringify(generateJSON(), null, 2)} height='200px' readOnly={true} stylingMode='outlined' />
            </div>

            {/* Divisor */}
            <div style={{ height: '1px', backgroundColor: '#e0e0e0', margin: '20px 0' }}></div>

            {/* Sección de Importación */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Importar Plantilla desde JSON</h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button
                    text='Limpiar'
                    icon='clear'
                    onClick={() => {
                      setImportJsonText('');
                      setImportValidationResult(null);
                    }}
                    disabled={!importJsonText.trim()}
                  />
                  <Button text='Importar Plantilla' icon='check' type='success' onClick={importFromJson} disabled={!importValidationResult?.isValid} />
                </div>
              </div>

              <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>Pega aquí un JSON válido de plantilla. El sistema validará automáticamente la estructura.</div>

              {/* Área de texto para pegar JSON */}
              <TextArea
                height='200px'
                value={importJsonText}
                onValueChanged={(e: any) => {
                  setImportJsonText(e.value || '');
                  validateImportJson(e.value || '');
                }}
                placeholder={`Pega aquí tu JSON de plantilla. Ejemplo:
                [
                  {
                    "Group": "EXAMEN GENERAL",
                    "KeyValue": "Aspecto",
                    "Label": "Aspecto",
                    "Type": "dxSelectBox",
                    "Items": ["NORMAL", "ALTERADO"],
                    "ColSpan": 2
                  }
                ]`}
                stylingMode='outlined'
              />

              {/* Panel de validación */}
              {importValidationResult && (
                <div
                  style={{
                    padding: '15px',
                    borderRadius: '6px',
                    backgroundColor: importValidationResult.isValid ? '#e8f5e8' : '#ffeaea',
                    border: `1px solid ${importValidationResult.isValid ? '#4caf50' : '#f44336'}`,
                    marginBottom: '15px',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                    }}>
                    <div
                      style={{
                        fontSize: '20px',
                        color: importValidationResult.isValid ? '#4caf50' : '#f44336',
                        lineHeight: 1,
                        marginTop: '2px',
                      }}>
                      {importValidationResult.isValid ? '✅' : '❌'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          color: importValidationResult.isValid ? '#2e7d32' : '#c62828',
                        }}>
                        {importValidationResult.isValid ? 'JSON Válido' : 'Errores Encontrados'}
                      </div>
                      <pre
                        style={{
                          fontSize: '13px',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          color: importValidationResult.isValid ? '#388e3c' : '#d32f2f',
                          lineHeight: '1.4',
                        }}>
                        {importValidationResult.message}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabItem>
      </TabPanel>

      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          gap: '10px',
        }}>
        <Button text='Guardar plantilla' icon='save' type='default' stylingMode='contained' onClick={saveTemplate} width={200} />
      </div>

      {/* Popup para nuevo grupo */}
      <Popup visible={newGroupPopupVisible} onHiding={closeNewGroupPopup} dragEnabled={false} hideOnOutsideClick={true} showTitle={true} title='Nuevo Grupo' width={400} height='auto'>
        <Form
          formData={{ groupName: currentGroupName }}
          labelLocation='top'
          showColonAfterLabel={false}
          onFieldDataChanged={(e) => {
            if (e.dataField === 'groupName') {
              setCurrentGroupName(e.value || '');
            }
          }}>
          <Item
            dataField='groupName'
            isRequired={true}
            editorType='dxTextBox'
            editorOptions={{
              placeholder: 'GRAM, EXAMEN MACRÓSCOPICO, EXAMEN MICROSCÓPICO...',
              maxLength: 50,
            }}
            validationRules={[
              {
                type: 'required',
                message: 'El nombre del grupo es requerido',
              },
            ]}>
            <Label text='Nombre del grupo' />
          </Item>
        </Form>
        {/* Botones en el toolbar del Popup */}
        <ToolbarItem
          widget='dxButton'
          toolbar='bottom'
          location='after'
          options={{
            text: 'Cancelar',
            stylingMode: 'outlined',
            onClick: closeNewGroupPopup,
          }}
        />

        <ToolbarItem
          widget='dxButton'
          toolbar='bottom'
          location='after'
          options={{
            text: 'Crear Grupo',
            type: 'default',
            stylingMode: 'contained',
            onClick: () => {
              if (!currentGroupName.trim()) {
                newToast('WARNING', 'Por favor ingrese un nombre para el grupo', 2);
                return;
              }
              addGroup(currentGroupName);
            },
          }}
        />
      </Popup>

      {/* Popup para nuevo parámetro personalizado */}
      <Popup
        visible={newParameterPopupVisible}
        onHiding={() => {
          setNewParameterPopupVisible(false);
          setNewParameterFormData({
            KeyValue: '',
            Label: '',
            Type: 'dxTextBox',
            Items: [],
            ColSpan: 2,
            Orden: 1,
          });
        }}
        dragEnabled={false}
        hideOnOutsideClick={true}
        showTitle={true}
        title='Agregar Parámetro Personalizado'
        width={600}
        height='auto'>
        <Form
          formData={newParameterFormData}
          labelLocation='top'
          showColonAfterLabel={false}
          onFieldDataChanged={(e: any) => {
            setNewParameterFormData((prev) => ({
              ...prev,
              [e.dataField]: e.value,
            }));
          }}>
          <Item
            dataField='KeyValue'
            isRequired={true}
            editorType='dxTextBox'
            editorOptions={{
              placeholder: 'Ej: Reaccion_leucocitaria',
              onKeyDown: (e: any) => {
                const key = e.event?.key;
                const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];

                if (allowedControlKeys.includes(key || '') || e.event?.ctrlKey || e.event?.metaKey) {
                  return;
                }

                if (key && !/^[a-zA-Z0-9_]$/.test(key)) {
                  e.event?.preventDefault();
                }
              },
            }}>
            <Label text='Llave' />
          </Item>

          <Item
            dataField='Label'
            isRequired={true}
            editorType='dxTextBox'
            editorOptions={{
              placeholder: 'Ej: Reacción leucocitaria',
            }}>
            <Label text='Nombre visible' />
          </Item>

          <Item
            dataField='Type'
            editorType='dxSelectBox'
            editorOptions={{
              dataSource: typeControls,
              valueExpr: 'value',
              displayExpr: 'text',
            }}>
            <Label text='Tipo de control' />
          </Item>

          <Item
            dataField='ColSpan'
            editorType='dxNumberBox'
            editorOptions={{
              defaultValue: 2,
              min: 1,
              max: 2,
            }}>
            <Label text='Ancho (ColSpan)' />
          </Item>

          <Item
            dataField='Items'
            editorType='dxTagBox'
            editorOptions={{
              dataSource: presetItems,
              acceptCustomValue: true,
              placeholder: 'Seleccione o escriba las opciones',
              multiline: true,
            }}>
            <Label text='Opciones (solo para listas desplegables)' />
          </Item>
        </Form>
        <ToolbarItem
          widget='dxButton'
          toolbar='bottom'
          location='after'
          options={{
            text: 'Cancelar',
            stylingMode: 'outlined',
            onClick: () => {
              setNewParameterPopupVisible(false);
              setNewParameterFormData({
                KeyValue: '',
                Label: '',
                Type: 'dxTextBox',
                Items: [],
                ColSpan: 2,
                Orden: 1,
              });
            },
          }}
        />

        <ToolbarItem
          widget='dxButton'
          toolbar='bottom'
          location='after'
          options={{
            text: 'Agregar Parámetro',
            type: 'default',
            stylingMode: 'contained',
            onClick: () => {
              if (selectedGroupId && newParameterFormData.KeyValue.trim() && newParameterFormData.Label.trim()) {
                const param: Parameter = {
                  id: `param_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  KeyValue: newParameterFormData.KeyValue.trim(),
                  Label: newParameterFormData.Label.trim(),
                  Type: newParameterFormData.Type,
                  Items: newParameterFormData.Items,
                  ColSpan: newParameterFormData.ColSpan,
                  Orden: selectedGroup ? selectedGroup.parameters.length + 1 : 1,
                };
                addParameter(selectedGroupId, param);
              } else {
                notify('Por favor complete los campos obligatorios (Llave y Nombre)', 'warning', 3000);
              }
            },
          }}
        />
      </Popup>

      {/* Popup para grupos predefinidos */}
      <Popup visible={presetGroupPopupVisible} onHiding={() => setPresetGroupPopupVisible(false)} dragEnabled={false} hideOnOutsideClick={true} showTitle={true} title='Seleccionar Grupo Predefinido' width={500} height={400}>
        <div style={{ padding: '10px' }}>
          <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>Selecciona un grupo base para comenzar. Podrás modificar el nombre y parámetros después.</div>

          {presetGroups.map((preset, index) => (
            <div
              key={index}
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => addPresetGroup(preset)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#2196f3';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>{preset.name}</div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>{preset.parameters.length} parámetros incluidos</div>
              <div style={{ fontSize: '12px', color: '#888' }}>{preset.parameters.map((p) => p.Label).join(', ')}</div>
              <div style={{ marginTop: '10px' }}>
                <Button
                  text='Usar este grupo'
                  type='default'
                  stylingMode='contained'
                  onClick={(e: any) => {
                    e.event?.stopPropagation();
                    addPresetGroup(preset);
                  }}
                  width='100%'
                />
              </div>
            </div>
          ))}
        </div>
      </Popup>
      {/* Popup de confirmación de importación */}
      <Popup visible={confirmImportPopupVisible} onHiding={cancelImport} dragEnabled={false} hideOnOutsideClick={false} showTitle={true} title='Confirmar Importación' width={500} height={350}>
        <div style={{ padding: '20px' }}>
          {/* Icono y título */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div
              style={{
                fontSize: '48px',
                marginBottom: '10px',
                color: '#ff9800',
              }}>
              ⚠️
            </div>
            <h3
              style={{
                margin: 0,
                marginBottom: '10px',
                color: '#333',
                fontSize: '18px',
              }}>
              ¿Confirmar importación de plantilla?
            </h3>
          </div>

          {/* Información de la importación */}
          <div
            style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px',
            }}>
            <div style={{ marginBottom: '10px' }}>
              <strong style={{ color: '#495057' }}>Datos a importar:</strong>
            </div>

            <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
              <div
                style={{
                  backgroundColor: '#e3f2fd',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1976d2',
                }}>
                📁 {importValidationResult?.stats?.groups || 0} grupos
              </div>
              <div
                style={{
                  backgroundColor: '#e8f5e8',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#388e3c',
                }}>
                ⚙️ {importValidationResult?.stats?.parameters || 0} parámetros
              </div>
            </div>

            {importValidationResult?.stats && importValidationResult.stats.groups > 0 && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                <strong>Grupos:</strong>{' '}
                {Array.from(new Set(importValidationResult.data?.map((item: any) => item.Group) || []))
                  .slice(0, 3)
                  .join(', ')}
                {importValidationResult.stats.groups > 3 && ` y ${importValidationResult.stats.groups - 3} más...`}
              </div>
            )}
          </div>

          {/* Advertencia */}
          <div
            style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '25px',
              fontSize: '14px',
              color: '#856404',
            }}>
            <strong>⚠️ Importante:</strong> Esta acción reemplazará completamente la configuración actual de la plantilla.
          </div>

          {/* Botones */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}>
            <Button text='Cancelar' onClick={cancelImport} stylingMode='outlined' type='normal' width={100} />
            <Button text='Importar Plantilla' onClick={confirmImport} type='default' stylingMode='contained' width={140} icon='check' />
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default TemplateConfigurationImproved;
